// File: frontend/src/components/RelationshipDiagram.js
import React, { useState, useCallback, useEffect, useMemo } from 'react';
import ReactFlow, {
  MiniMap,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  Handle,
  Position,
  MarkerType,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { User, Database, Mail, Phone, Globe, MapPin, Hash, Link, UserPlus, X, Edit2, Save, AlertCircle, Trash2, Bug } from 'lucide-react';

// Debug component to show data structure
const DebugPanel = ({ people, nodes, edges, show }) => {
  if (!show) return null;
  
  return (
    <div className="absolute bottom-4 left-4 z-20 bg-white dark:bg-slate-800 rounded-lg shadow-lg p-4 max-w-md max-h-96 overflow-auto">
      <h3 className="font-bold mb-2">Debug Information</h3>
      <div className="space-y-2 text-xs">
        <div>
          <strong>People Count:</strong> {people.length}
        </div>
        <div>
          <strong>Nodes Count:</strong> {nodes.length}
        </div>
        <div>
          <strong>Edges Count:</strong> {edges.length}
        </div>
        <details>
          <summary className="cursor-pointer font-medium">People with Connections</summary>
          <pre className="mt-2 bg-gray-100 dark:bg-slate-700 p-2 rounded overflow-auto">
            {JSON.stringify(
              people.filter(p => p.connections && p.connections.length > 0).map(p => ({
                id: p.id,
                name: `${p.first_name} ${p.last_name}`,
                connections: p.connections
              })), 
              null, 
              2
            )}
          </pre>
        </details>
        <details>
          <summary className="cursor-pointer font-medium">All Edges</summary>
          <pre className="mt-2 bg-gray-100 dark:bg-slate-700 p-2 rounded overflow-auto">
            {JSON.stringify(edges.map(e => ({
              id: e.id,
              source: e.source,
              target: e.target,
              type: e.data?.type
            })), null, 2)}
          </pre>
        </details>
      </div>
    </div>
  );
};

// Custom node component with handles
const PersonNode = ({ data, selected }) => {
  const initials = data.label.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || '??';
  
  return (
    <>
      <Handle type="target" position={Position.Top} style={{ background: '#555' }} />
      <div className={`px-4 py-3 shadow-lg rounded-lg border-2 ${selected ? 'border-blue-500 ring-2 ring-blue-200' : 'border-gray-300 dark:border-slate-600'} bg-white dark:bg-slate-800 cursor-pointer min-w-[200px]`}>
        <div className="flex items-center space-x-3">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold flex-shrink-0 ${
            data.category === 'Person of Interest' ? 'bg-red-500' : 
            data.category === 'Client' ? 'bg-green-500' : 
            data.category === 'Witness' ? 'bg-yellow-500' : 
            data.category === 'Victim' ? 'bg-purple-500' :
            data.category === 'Suspect' ? 'bg-orange-500' :
            'bg-blue-500'
          }`}>
            {data.profilePicture ? (
              <img src={data.profilePicture} alt={data.label} className="w-full h-full rounded-full object-cover" />
            ) : (
              initials
            )}
          </div>
          <div className="min-w-0">
            <div className="text-sm font-medium text-gray-900 dark:text-slate-100 truncate">{data.label}</div>
            <div className="text-xs text-gray-500 dark:text-gray-400">{data.category || 'Unknown'}</div>
            {data.caseName && <div className="text-xs text-blue-600 truncate">{data.caseName}</div>}
          </div>
        </div>
        {data.status && (
          <div className={`mt-2 text-xs px-2 py-1 rounded-full inline-block ${
            data.status === 'Open' ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400' :
            data.status === 'Being Investigated' ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-400' :
            data.status === 'Closed' ? 'bg-gray-100 dark:bg-slate-700 text-gray-800 dark:text-slate-200' :
            'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-400'
          }`}>
            {data.status}
          </div>
        )}
      </div>
      <Handle type="source" position={Position.Bottom} style={{ background: '#555' }} />
    </>
  );
};

// Business node component
const BusinessNode = ({ data, selected }) => {
  const initials = (data.label || '?').slice(0, 2).toUpperCase();
  return (
    <>
      <Handle type="target" position={Position.Top} style={{ background: '#555' }} />
      <div className={`px-4 py-3 shadow-lg rounded-lg border-2 ${selected ? 'border-green-500 ring-2 ring-green-200' : 'border-green-400 dark:border-green-600'} bg-white dark:bg-slate-800 cursor-pointer min-w-[200px]`}>
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 rounded-lg bg-green-500 flex items-center justify-center text-white font-semibold flex-shrink-0 text-sm">
            {initials}
          </div>
          <div className="min-w-0">
            <div className="text-sm font-medium text-gray-900 dark:text-slate-100 truncate">{data.label}</div>
            <div className="text-xs text-green-600 dark:text-green-400">Business</div>
            {data.status && <div className="text-xs text-gray-500">{data.status}</div>}
          </div>
        </div>
      </div>
      <Handle type="source" position={Position.Bottom} style={{ background: '#555' }} />
    </>
  );
};

// Node type mapping
const nodeTypes = {
  person: PersonNode,
  business: BusinessNode,
};

// Edge styles
const edgeStyles = {
  family:     { stroke: '#10b981', strokeWidth: 3,              label: 'Family' },
  friend:     { stroke: '#3b82f6', strokeWidth: 2,              label: 'Friend' },
  enemy:      { stroke: '#ef4444', strokeWidth: 2, strokeDasharray: '5 5', label: 'Enemy' },
  associate:  { stroke: '#6b7280', strokeWidth: 2,              label: 'Associate' },
  employer:      { stroke: '#8b5cf6', strokeWidth: 2, strokeDasharray: '5 5', label: 'Employer/Employee' },
  colleague:     { stroke: '#0ea5e9', strokeWidth: 2,                        label: 'เพื่อนร่วมงาน' },
  employed_at:   { stroke: '#059669', strokeWidth: 2,                        label: 'Employed At' },
  linked_business: { stroke: '#64748b', strokeWidth: 1.5, strokeDasharray: '6 3', label: 'Linked Business' },
  suspect:    { stroke: '#ef4444', strokeWidth: 3,              label: 'Suspect Connection' },
  witness:    { stroke: '#f59e0b', strokeWidth: 2, strokeDasharray: '3 3', label: 'Witness' },
  victim:     { stroke: '#ec4899', strokeWidth: 2,              label: 'Victim' },
  // B2B types
  partner:    { stroke: '#f59e0b', strokeWidth: 2,              label: 'Partner' },
  subsidiary: { stroke: '#06b6d4', strokeWidth: 2,              label: 'Subsidiary/Parent' },
  competitor: { stroke: '#dc2626', strokeWidth: 2, strokeDasharray: '4 4', label: 'Competitor' },
  supplier:   { stroke: '#16a34a', strokeWidth: 2,              label: 'Supplier' },
  client:     { stroke: '#2563eb', strokeWidth: 2,              label: 'Client' },
  affiliate:  { stroke: '#9333ea', strokeWidth: 2,              label: 'Affiliate' },
  investor:   { stroke: '#ea580c', strokeWidth: 2,              label: 'Investor' },
  other:      { stroke: '#6b7280', strokeWidth: 2,              label: 'Other' },
};

const RelationshipDiagram = ({ 
  people = [], 
  selectedPersonId = null, 
  onUpdateConnection,
  onDeleteConnection,
  showOsintData = false,
  layoutType = 'hierarchical' 
}) => {
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [showDebug, setShowDebug] = useState(false);
  const [selectedNodes, setSelectedNodes] = useState([]);
  const [highlightedNodeId, setHighlightedNodeId] = useState(null);
  const [highlightedEdgeId, setHighlightedEdgeId] = useState(null);
  const [isAddingConnection, setIsAddingConnection] = useState(false);
  const [connectionModal, setConnectionModal] = useState(null);
  const [connectionType, setConnectionType] = useState('associate');
  const [connectionNote, setConnectionNote] = useState('');

  // Helper function to get full name
  const getFullName = (person) => {
    if (!person) return 'Unknown';
    return `${person.first_name || ''} ${person.last_name || ''}`.trim() || 'Unknown';
  };

  // Layout algorithms
  const applyLayout = useCallback((nodes, edges, type) => {
    const nodesCopy = [...nodes];
    
    switch (type) {
      case 'hierarchical': {
        const levels = new Map();
        const visited = new Set();
        
        // Find nodes with no incoming edges (roots)
        const roots = nodesCopy.filter(node => 
          !edges.some(edge => edge.target === node.id)
        );
        
        if (roots.length === 0 && nodesCopy.length > 0) {
          roots.push(nodesCopy[0]);
        }
        
        // BFS to assign levels
        const queue = roots.map(root => ({ node: root, level: 0 }));
        
        while (queue.length > 0) {
          const { node, level } = queue.shift();
          if (visited.has(node.id)) continue;
          
          visited.add(node.id);
          if (!levels.has(level)) levels.set(level, []);
          levels.get(level).push(node);
          
          // Find children
          const children = edges
            .filter(edge => edge.source === node.id)
            .map(edge => nodesCopy.find(n => n.id === edge.target))
            .filter(child => child && !visited.has(child.id));
          
          children.forEach(child => {
            queue.push({ node: child, level: level + 1 });
          });
        }
        
        // Add unvisited nodes
        nodesCopy.forEach(node => {
          if (!visited.has(node.id)) {
            const level = levels.size;
            if (!levels.has(level)) levels.set(level, []);
            levels.get(level).push(node);
          }
        });
        
        // Position nodes
        const levelHeight = 200;
        const nodeWidth = 300;
        
        levels.forEach((levelNodes, level) => {
          levelNodes.forEach((node, index) => {
            node.position = {
              x: (index - (levelNodes.length - 1) / 2) * nodeWidth,
              y: level * levelHeight
            };
          });
        });
        break;
      }
      
      case 'circular': {
        const radius = Math.min(400, Math.max(200, nodesCopy.length * 40));
        const center = { x: 0, y: 0 };
        
        nodesCopy.forEach((node, index) => {
          const angle = (2 * Math.PI * index) / nodesCopy.length - Math.PI / 2;
          node.position = {
            x: center.x + radius * Math.cos(angle),
            y: center.y + radius * Math.sin(angle)
          };
        });
        break;
      }
      
      case 'grid': {
        const cols = Math.ceil(Math.sqrt(nodesCopy.length));
        const nodeSpacing = 300;
        
        nodesCopy.forEach((node, index) => {
          const row = Math.floor(index / cols);
          const col = index % cols;
          node.position = {
            x: col * nodeSpacing,
            y: row * nodeSpacing
          };
        });
        break;
      }
    }
    
    return nodesCopy;
  }, []);

  // Build nodes and edges from people data
  useEffect(() => {
    console.log('=== RelationshipDiagram: Building diagram ===');
    console.log('People count:', people.length);
    console.log('Layout type:', layoutType);
    
    const newNodes = [];
    const newEdges = [];
    const edgeMap = new Map(); // To track and avoid duplicate edges
    
    // Create nodes for each person/business
    const nodeIdSet = new Set();
    people.forEach(person => {
      const nodeId = `person-${person.id}`;
      const isBusiness = person.type === 'business';
      const node = {
        id: nodeId,
        type: isBusiness ? 'business' : 'person',
        position: { x: 0, y: 0 },
        data: {
          label: getFullName(person),
          category: person.category,
          status: person.status,
          caseName: person.case_name,
          profilePicture: person.profile_picture_url,
          personId: person.id,
          raw: person
        }
      };

      newNodes.push(node);
      nodeIdSet.add(nodeId);
      console.log(`Created node: ${nodeId} - ${node.data.label}`);
    });
    
    // Create edges from connections
    people.forEach(person => {
      if (!person.connections || !Array.isArray(person.connections)) {
        return;
      }
      
      console.log(`Processing connections for ${person.id} - ${getFullName(person)}: ${person.connections.length} connections`);
      
      person.connections.forEach((connection, idx) => {
        // Validate connection
        if (!connection || typeof connection.person_id === 'undefined') {
          console.warn(`Invalid connection at index ${idx} for person ${person.id}`);
          return;
        }
        
        const targetPersonId = connection.person_id;
        const targetPerson = people.find(p => p.id === targetPersonId);
        
        if (!targetPerson) {
          console.warn(`Target person ${targetPersonId} not found for connection from ${person.id}`);
          return;
        }
        
        const sourceNodeId = `person-${person.id}`;
        const targetNodeId = `person-${targetPersonId}`;
        
        // Key includes type so same pair can have multiple edges with different types
        const edgeKey = `${Math.min(person.id, targetPersonId)}-${Math.max(person.id, targetPersonId)}-${connection.type}`;

        if (!edgeMap.has(edgeKey)) {
          const style = edgeStyles[connection.type] || edgeStyles.other;
          const edge = {
            id: `edge-${person.id}-to-${targetPersonId}`,
            source: sourceNodeId,
            target: targetNodeId,
            type: 'smoothstep',
            animated: connection.type === 'suspect',
            label: connection.note || style.label,
            labelStyle: { 
              fontSize: 12, 
              fontWeight: 500,
              fill: '#1f2937'
            },
            labelBgStyle: { 
              fill: '#ffffff',
              fillOpacity: 0.8
            },
            style: {
              stroke: style.stroke,
              strokeWidth: style.strokeWidth,
              strokeDasharray: style.strokeDasharray || '0',
              cursor: 'pointer'
            },
            markerEnd: {
              type: MarkerType.ArrowClosed,
              color: style.stroke,
              width: 15,
              height: 15
            },
            data: {
              type: connection.type,
              note: connection.note,
              sourcePersonId: person.id,
              targetPersonId: targetPersonId
            }
          };
          
          newEdges.push(edge);
          edgeMap.set(edgeKey, edge);
          console.log(`Created edge: ${edge.id} (${connection.type})`);
        } else {
          console.log(`Skipping duplicate edge: ${edgeKey}`);
        }
      });
    });
    
    // Create B2B edges from business_connections
    people.forEach(entity => {
      if (entity.type !== 'business') return;
      const conns = entity.businessData?.business_connections || entity.raw?.business_connections || [];
      conns.forEach(conn => {
        const sourceNodeId = `person-${entity.id}`;
        const targetNodeId = `person-business-${conn.target_id}`;
        if (!nodeIdSet.has(targetNodeId)) return;
        const edgeKey = `b2b-${Math.min(entity.businessData?.id || 0, conn.target_id)}-${Math.max(entity.businessData?.id || 0, conn.target_id)}-${conn.relationship_type}`;
        if (edgeMap.has(edgeKey)) return;
        const style = edgeStyles[conn.relationship_type] || edgeStyles.other;
        newEdges.push({
          id: `edge-biz-${entity.businessData?.id}-to-${conn.target_id}`,
          source: sourceNodeId,
          target: targetNodeId,
          type: 'smoothstep',
          animated: false,
          label: style.label,
          labelStyle: { fontSize: 12, fontWeight: 500, fill: '#1f2937' },
          labelBgStyle: { fill: '#ffffff', fillOpacity: 0.8 },
          style: { stroke: style.stroke, strokeWidth: style.strokeWidth, strokeDasharray: style.strokeDasharray || '0' },
          markerEnd: { type: MarkerType.ArrowClosed, color: style.stroke, width: 15, height: 15 },
          data: { type: conn.relationship_type, note: conn.note }
        });
        edgeMap.set(edgeKey, true);
      });
    });

    // Create business link edges from linked_businesses array
    people.forEach(person => {
      if (person.type === 'business') return;
      const empList = person.linked_businesses || [];
      empList.forEach(emp => {
        if (!emp.business_id) return;

        const sourceNodeId = `person-${person.id}`;
        const targetNodeId = `person-business-${emp.business_id}`;
        if (!nodeIdSet.has(targetNodeId)) return;

        const isEmployed = emp.type === 'employed';
        const edgeKey = `${isEmployed ? 'employed' : 'linked'}-${person.id}-${emp.business_id}`;
        if (edgeMap.has(edgeKey)) return;

        const style = isEmployed ? edgeStyles.employed_at : edgeStyles.linked_business;
        const baseLabel = isEmployed ? 'Employed At' : 'Linked Business';
        const edgeLabel = emp.role
          ? `${emp.role}${emp.department ? ' · ' + emp.department : ''}`
          : baseLabel;

        newEdges.push({
          id: `edge-${isEmployed ? 'employed' : 'linked'}-${person.id}-to-biz-${emp.business_id}`,
          source: sourceNodeId,
          target: targetNodeId,
          type: 'smoothstep',
          animated: false,
          label: edgeLabel,
          labelStyle: { fontSize: 12, fontWeight: 500, fill: '#1f2937' },
          labelBgStyle: { fill: '#ffffff', fillOpacity: 0.8 },
          style: { stroke: style.stroke, strokeWidth: style.strokeWidth, strokeDasharray: style.strokeDasharray || '0' },
          markerEnd: { type: MarkerType.ArrowClosed, color: style.stroke, width: 15, height: 15 },
          data: { type: isEmployed ? 'employed_at' : 'linked_business', note: edgeLabel, sourcePersonId: person.id, targetPersonId: null }
        });
        edgeMap.set(edgeKey, true);
      });
    });

    console.log(`Total nodes created: ${newNodes.length}`);
    console.log(`Total edges created: ${newEdges.length}`);
    
    // Apply layout
    const layoutedNodes = applyLayout(newNodes, newEdges, layoutType);
    
    // Set the nodes and edges
    setNodes(layoutedNodes);
    setEdges(newEdges);
    
  }, [people, layoutType, setNodes, setEdges, applyLayout]);

  // Handle connection creation
  const onConnect = useCallback((params) => {
    console.log('onConnect called:', params);
    
    if (!params.source || !params.target) return;
    
    const sourcePersonId = parseInt(params.source.replace('person-', ''));
    const targetPersonId = parseInt(params.target.replace('person-', ''));
    
    if (isNaN(sourcePersonId) || isNaN(targetPersonId)) {
      console.error('Invalid person IDs:', sourcePersonId, targetPersonId);
      return;
    }
    
    const sourcePerson = people.find(p => p.id === sourcePersonId);
    const targetPerson = people.find(p => p.id === targetPersonId);
    
    if (sourcePerson && targetPerson) {
      setConnectionModal({
        sourceId: sourcePersonId,
        targetId: targetPersonId,
        sourceName: getFullName(sourcePerson),
        targetName: getFullName(targetPerson)
      });
    }
  }, [people]);

  // Handle node click
  const onNodeClick = useCallback((event, node) => {
    console.log('Node clicked:', node);

    if (isAddingConnection && node.type === 'person') {
      setSelectedNodes(prev => {
        if (prev.includes(node.id)) {
          return prev.filter(id => id !== node.id);
        } else if (prev.length < 2) {
          return [...prev, node.id];
        }
        return prev;
      });
    } else {
      setHighlightedEdgeId(null);
      setHighlightedNodeId(prev => prev === node.id ? null : node.id);
    }
  }, [isAddingConnection]);

  // Create connection between selected nodes
  const createConnection = useCallback(() => {
    if (selectedNodes.length === 2) {
      const sourceId = parseInt(selectedNodes[0].replace('person-', ''));
      const targetId = parseInt(selectedNodes[1].replace('person-', ''));
      
      const sourcePerson = people.find(p => p.id === sourceId);
      const targetPerson = people.find(p => p.id === targetId);
      
      if (sourcePerson && targetPerson) {
        setConnectionModal({
          sourceId,
          targetId,
          sourceName: getFullName(sourcePerson),
          targetName: getFullName(targetPerson)
        });
      }
    }
  }, [selectedNodes, people]);

  // Save connection
  const saveConnection = useCallback(async () => {
    if (connectionModal && onUpdateConnection) {
      console.log('Saving connection:', connectionModal);
      
      await onUpdateConnection(
        connectionModal.sourceId,
        connectionModal.targetId,
        connectionType,
        connectionNote
      );
      
      setConnectionModal(null);
      setConnectionType('associate');
      setConnectionNote('');
      setIsAddingConnection(false);
      setSelectedNodes([]);
    }
  }, [connectionModal, connectionType, connectionNote, onUpdateConnection]);

  // Compute which nodes should be highlighted based on node or edge selection
  const connectedNodeIds = useMemo(() => {
    if (highlightedNodeId) {
      const ids = new Set([highlightedNodeId]);
      edges.forEach(e => {
        if (e.source === highlightedNodeId) ids.add(e.target);
        if (e.target === highlightedNodeId) ids.add(e.source);
      });
      return ids;
    }
    if (highlightedEdgeId) {
      const edge = edges.find(e => e.id === highlightedEdgeId);
      if (edge) return new Set([edge.source, edge.target]);
    }
    return null;
  }, [highlightedNodeId, highlightedEdgeId, edges]);

  const styledEdges = useMemo(() => {
    if (!highlightedNodeId && !highlightedEdgeId) return edges;
    return edges.map(edge => {
      const connected = highlightedEdgeId
        ? edge.id === highlightedEdgeId
        : edge.source === highlightedNodeId || edge.target === highlightedNodeId;
      return {
        ...edge,
        style: {
          ...edge.style,
          strokeWidth: connected ? (edge.style?.strokeWidth || 2) + 2 : edge.style?.strokeWidth,
          opacity: connected ? 1 : 0.1,
        },
        animated: connected,
        zIndex: connected ? 10 : 0,
      };
    });
  }, [edges, highlightedNodeId, highlightedEdgeId]);

  // Handle edge click — highlight the edge and its endpoints
  const onEdgeClick = useCallback((event, edge) => {
    event.stopPropagation();
    setHighlightedNodeId(null);
    setHighlightedEdgeId(prev => prev === edge.id ? null : edge.id);
  }, []);

  return (
    <div className="h-full w-full relative" style={{ minHeight: '400px' }}>
      {/* Toolbar */}
      <div className="absolute top-4 left-4 z-10 bg-white dark:bg-slate-800 rounded-lg shadow-lg p-3 space-y-2">
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setShowDebug(!showDebug)}
            className={`px-3 py-2 rounded-md text-sm font-medium flex items-center space-x-2 ${
              showDebug ? 'bg-orange-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
            title="Toggle Debug Panel"
          >
            <Bug className="w-4 h-4" />
          </button>
          
          <button
            onClick={() => {
              setIsAddingConnection(!isAddingConnection);
              setSelectedNodes([]);
            }}
            className={`px-3 py-2 rounded-md text-sm font-medium flex items-center space-x-2 ${
              isAddingConnection 
                ? 'bg-blue-600 text-white' 
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <UserPlus className="w-4 h-4" />
            <span>{isAddingConnection ? 'Cancel' : 'Add Connection'}</span>
          </button>
          
          {isAddingConnection && selectedNodes.length === 2 && (
            <button
              onClick={createConnection}
              className="px-3 py-2 bg-green-600 text-white rounded-md text-sm font-medium flex items-center space-x-2 hover:bg-green-700"
            >
              <Link className="w-4 h-4" />
              <span>Connect</span>
            </button>
          )}
        </div>
        
        {isAddingConnection && (
          <div className="text-sm text-gray-600 dark:text-gray-400">
            {selectedNodes.length === 0 && 'Click on two people to connect them'}
            {selectedNodes.length === 1 && 'Select one more person'}
            {selectedNodes.length === 2 && 'Click "Connect" to create relationship'}
          </div>
        )}
        
        {/* Layout selector */}
        <select
          value={layoutType}
          onChange={(e) => {
            const newLayout = e.target.value;
            const layoutedNodes = applyLayout(nodes, edges, newLayout);
            setNodes(layoutedNodes);
          }}
          className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-slate-600 rounded-md"
        >
          <option value="hierarchical">Hierarchical Layout</option>
          <option value="circular">Circular Layout</option>
          <option value="grid">Grid Layout</option>
        </select>
        
        <div className="text-xs text-gray-500 dark:text-slate-400 space-y-1">
          <div>Nodes: {nodes.length}</div>
          <div>Connections: {edges.length}</div>
        </div>
      </div>

      {/* Legend */}
      <div className="absolute top-4 right-4 z-10 bg-white dark:bg-slate-800 rounded-lg shadow-lg p-3">
        <h4 className="text-sm font-semibold mb-2">Connection Types</h4>
        <div className="space-y-1">
          {Object.entries(edgeStyles).map(([type, style]) => (
            <div key={type} className="flex items-center space-x-2">
              <svg width="30" height="10">
                <line 
                  x1="0" 
                  y1="5" 
                  x2="30" 
                  y2="5" 
                  stroke={style.stroke}
                  strokeWidth={style.strokeWidth}
                  strokeDasharray={style.strokeDasharray || '0'}
                />
              </svg>
              <span className="text-xs text-gray-600 dark:text-gray-400">{style.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* React Flow */}
      <ReactFlow
        nodes={nodes.map(node => ({
          ...node,
          selected: selectedNodes.includes(node.id),
          style: connectedNodeIds
            ? { ...node.style, opacity: connectedNodeIds.has(node.id) ? 1 : 0.25 }
            : node.style,
        }))}
        edges={styledEdges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={isAddingConnection ? onConnect : undefined}
        onNodeClick={onNodeClick}
        onEdgeClick={onEdgeClick}
        onPaneClick={() => { setHighlightedNodeId(null); setHighlightedEdgeId(null); }}
        nodeTypes={nodeTypes}
        fitView
        fitViewOptions={{
          padding: 0.2,
          includeHiddenNodes: false
        }}
        defaultEdgeOptions={{
          type: 'smoothstep',
          animated: false
        }}
        connectionMode="loose"
        className="bg-gray-50 dark:bg-slate-900"
        minZoom={0.1}
        maxZoom={2}
      >
        <Background variant="dots" gap={12} size={1} />
        <Controls />
        <MiniMap 
          nodeStrokeWidth={3}
          zoomable
          pannable
        />
      </ReactFlow>

      {/* Debug Panel */}
      <DebugPanel 
        people={people} 
        nodes={nodes} 
        edges={edges} 
        show={showDebug} 
      />

      {/* Connection Modal */}
      {connectionModal && (
        <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center z-20">
          <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl p-6 w-96">
            <h3 className="text-lg font-semibold mb-4">Create Connection</h3>
            
            <div className="mb-4">
              <p className="text-sm text-gray-600 dark:text-slate-400 mb-2">
                Connecting: <span className="font-medium">{connectionModal.sourceName}</span> → <span className="font-medium">{connectionModal.targetName}</span>
              </p>
            </div>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
                Relationship Type
              </label>
              <select
                value={connectionType}
                onChange={(e) => setConnectionType(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-md"
              >
                {Object.entries(edgeStyles).map(([type, style]) => (
                  <option key={type} value={type}>{style.label}</option>
                ))}
              </select>
            </div>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
                Notes (optional)
              </label>
              <textarea
                value={connectionNote}
                onChange={(e) => setConnectionNote(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-md"
                rows="3"
                placeholder="Add any relevant notes about this relationship..."
              />
            </div>
            
            <div className="flex justify-end space-x-2">
              <button
                onClick={() => {
                  setConnectionModal(null);
                  setConnectionType('associate');
                  setConnectionNote('');
                }}
                className="px-4 py-2 text-gray-700 dark:text-slate-300 bg-gray-100 dark:bg-slate-700 rounded-md hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={saveConnection}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Save Connection
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RelationshipDiagram;